import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { getStats } from "../lib/stats-api.js";
import { getAlerts, type AlertDto } from "../lib/alerts-api.js";
import { StatAlertRow } from "../components/StatAlertRow.js";

export function StatsScreen(): React.ReactElement {
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ["stats"],
    queryFn: getStats,
  });

  const { data: alerts = [], isLoading: isLoadingAlerts } = useQuery({
    queryKey: ["alerts"],
    queryFn: getAlerts,
  });

  const isLoading = isLoadingStats || isLoadingAlerts;

  const weeklyTotal = stats?.weekly?.total ?? 0;
  const monthlyTotal = stats?.monthly?.total ?? 0;

  return (
    <div data-testid="stats-screen" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Header */}
      <div
        style={{
          height: "56px",
          display: "flex",
          alignItems: "center",
          padding: "4px 4px",
          flexShrink: 0,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "22px",
            lineHeight: "26px",
            fontWeight: 700,
            color: "var(--color-graphite)",
            letterSpacing: "-0.02em",
          }}
        >
          Статистика доставок
        </h1>
      </div>

      {/* Overview Totals Card */}
      <div
        data-testid="stats-overview-card"
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px",
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "13px", color: "var(--color-slate)", fontWeight: 500 }}>
            За 7 дней
          </div>
          <div
            data-testid="weekly-total"
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "var(--color-graphite)",
              marginTop: "4px",
            }}
          >
            {isLoading ? "..." : weeklyTotal}
          </div>
          <div style={{ fontSize: "11px", color: "var(--color-slate)", marginTop: "2px" }}>
            вакансий
          </div>
        </div>

        <div style={{ width: "1px", height: "48px", backgroundColor: "var(--color-frost-border)" }} />

        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "13px", color: "var(--color-slate)", fontWeight: 500 }}>
            За 30 дней
          </div>
          <div
            data-testid="monthly-total"
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "var(--color-graphite)",
              marginTop: "4px",
            }}
          >
            {isLoading ? "..." : monthlyTotal}
          </div>
          <div style={{ fontSize: "11px", color: "var(--color-slate)", marginTop: "2px" }}>
            вакансий
          </div>
        </div>
      </div>

      {/* Per Alert Breakdown Section */}
      <div>
        <h2
          style={{
            margin: "0 0 12px 4px",
            fontSize: "18px",
            fontWeight: 600,
            color: "var(--color-graphite)",
          }}
        >
          По профилям
        </h2>

        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {[1, 2].map((k) => (
              <div
                key={k}
                style={{
                  height: "80px",
                  backgroundColor: "var(--color-paper)",
                  borderRadius: "var(--radius-card)",
                  boxShadow: "var(--shadow-card)",
                }}
              />
            ))}
          </div>
        ) : alerts.length === 0 ? (
          <div
            data-testid="stats-empty-alerts"
            style={{
              backgroundColor: "var(--color-paper)",
              borderRadius: "var(--radius-card)",
              boxShadow: "var(--shadow-floating)",
              padding: "32px 20px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <p style={{ margin: 0, fontSize: "14px", color: "var(--color-slate)" }}>
              Добавьте новый профиль, чтобы начать получать статистику.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {alerts.map((alert: AlertDto) => {
              const alertKey = alert.id.toString();
              const weeklyCount = stats?.weekly?.byAlert?.[alertKey] ?? 0;
              const monthlyCount = stats?.monthly?.byAlert?.[alertKey] ?? 0;

              return (
                <StatAlertRow
                  key={alert.id}
                  alertId={alert.id}
                  specialization={alert.specialization}
                  weeklyCount={weeklyCount}
                  monthlyCount={monthlyCount}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Helpful info box */}
      {!isLoading && alerts.length > 0 && monthlyTotal === 0 && (
        <div
          data-testid="stats-zero-note"
          style={{
            backgroundColor: "var(--color-warning-surface)",
            borderRadius: "var(--radius-card)",
            padding: "16px 20px",
            display: "flex",
            gap: "12px",
            alignItems: "flex-start",
            border: "1px solid rgba(152, 94, 0, 0.2)",
          }}
        >
          <Info size={18} color="var(--color-warning)" style={{ flexShrink: 0, marginTop: "2px" }} />
          <p style={{ margin: 0, fontSize: "13px", color: "var(--color-warning)", lineHeight: 1.4 }}>
            Если вакансии долго не приходят, попробуйте убрать часть фильтров по навыкам, опыту или стоп-словам.
          </p>
        </div>
      )}
    </div>
  );
}

export default StatsScreen;
